import axios from 'axios';
import { getApiBaseUrl } from '../config/api';
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from '../lib/authStorage';

const baseURL = getApiBaseUrl();

export const api = axios.create({
  baseURL,
  // Cookie-based JWT (commented — cross-origin Cloudflare + Cloud Run uses Bearer + localStorage):
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

function isAuthRefreshRequest(config) {
  const url = config?.url || '';
  return url.includes('/v1/admin/auth/refresh');
}

function isAuthLoginRequest(config) {
  const url = config?.url || '';
  return url.includes('/v1/admin/auth/login');
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;
    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    if (isAuthRefreshRequest(original) || isAuthLoginRequest(original)) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshPromise) {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw error;
        }
        refreshPromise = api
          .post('/v1/admin/auth/refresh', { refreshToken })
          .then((res) => {
            const body = res.data?.data ?? res.data;
            if (body?.accessToken && body?.refreshToken) {
              setAuthTokens({
                accessToken: body.accessToken,
                refreshToken: body.refreshToken,
              });
            }
            return res;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      const accessToken = getAccessToken();
      if (accessToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${accessToken}`;
      }
      return api(original);
    } catch {
      clearAuthTokens();
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const isAuthPage = path.startsWith('/login') || path.startsWith('/brand/login');
        if (!isAuthPage) {
          window.location.assign(path.startsWith('/brand') ? '/brand/login' : '/login');
        }
      }
      return Promise.reject(error);
    }
  }
);

export class ApiError extends Error {
  constructor(message, code, fieldErrors) {
    super(message || 'Request failed');
    this.name = 'ApiError';
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

export function unwrap(body) {
  if (!body || typeof body !== 'object') return body;
  if (body.success === false) {
    const err = body.error || {};
    throw new ApiError(err.message, err.code, err.fieldErrors);
  }
  if (Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data;
  }
  return body;
}
